/**
 * Token 使用统计（旧 views/system/TokenUsageViewSimple.vue）：汇总卡片、趋势、模型分布、供应商成本、用户和模型排行、明细弹窗
 */
import { useState } from 'react'
import { App, Button, Card, Col, DatePicker, Row, Space, Statistic, Table, Tag, Typography, type TableColumnsType } from 'antd'
import { ApiOutlined, ReloadOutlined, UnorderedListOutlined, UserOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { PageHeader } from '@/components/PageHeader'
import { providerColor, rankColor } from './constants'
import {
  toDateRangeParams,
  trendDays,
  useTokenStats,
  type ModelRankingRow,
  type ProviderCost,
  type UserRankingRow
} from './useTokenStats'
import { ModelDistributionChart, TrendChart } from './TokenUsageCharts'
import { TokenUsageDetailsModal } from './TokenUsageDetailsModal'
import styles from './TokenUsagePage.module.css'

type DateRange = [Dayjs, Dayjs]

// 预设以打开下拉时的当前时间为准
function rangePresets(): Array<{ label: string; value: DateRange }> {
  return [7, 30, 90].map((days) => ({ label: `最近${days}天`, value: [dayjs().subtract(days, 'day'), dayjs()] }))
}

function Cost({ value, digits }: Readonly<{ value: number; digits: number }>) {
  return <Typography.Text type="danger">${(value ?? 0).toFixed(digits)}</Typography.Text>
}

const rankColumn = {
  title: '排名',
  key: 'rank',
  width: 80,
  render: (_: unknown, __: unknown, index: number) => <Tag color={rankColor(index)}>{index + 1}</Tag>
}

const providerColumns: TableColumnsType<ProviderCost> = [
  {
    title: '供应商',
    dataIndex: 'provider',
    key: 'provider',
    render: (provider: string) => <Tag color={providerColor(provider)}>{provider.toUpperCase()}</Tag>
  },
  { title: '请求次数', dataIndex: 'requests', key: 'requests', sorter: (a, b) => a.requests - b.requests },
  { title: 'Token总数', dataIndex: 'tokens', key: 'tokens', sorter: (a, b) => a.tokens - b.tokens },
  {
    title: '总成本',
    dataIndex: 'cost',
    key: 'cost',
    sorter: (a, b) => a.cost - b.cost,
    render: (cost: number) => (
      <strong>
        <Cost value={cost} digits={4} />
      </strong>
    )
  }
]

const userColumns: TableColumnsType<UserRankingRow> = [
  rankColumn,
  { title: '用户', key: 'user', dataIndex: 'username' },
  { title: '请求次数', dataIndex: 'total_requests', key: 'total_requests' },
  { title: 'Token总数', dataIndex: 'total_tokens', key: 'total_tokens' },
  {
    title: '总成本',
    dataIndex: 'total_cost',
    key: 'total_cost',
    render: (cost: number) => <Cost value={cost} digits={4} />
  }
]

const modelColumns: TableColumnsType<ModelRankingRow> = [
  rankColumn,
  { title: '模型', key: 'model', dataIndex: 'model' },
  { title: '请求次数', dataIndex: 'requests', key: 'requests' },
  { title: 'Token总数', dataIndex: 'tokens', key: 'tokens' },
  { title: '总成本', dataIndex: 'cost', key: 'cost', render: (cost: number) => <Cost value={cost} digits={4} /> }
]

export function TokenUsagePage() {
  const { message } = App.useApp()
  const [dateRange, setDateRange] = useState<DateRange>(() => [dayjs().subtract(30, 'day'), dayjs()])
  // 明细弹窗第一次打开后保持挂载，筛选条件在本页内保留
  const [detailsMounted, setDetailsMounted] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const { global, userRanking, modelRanking, trend } = useTokenStats(toDateRangeParams(dateRange), trendDays(dateRange))
  const summary = global.data?.summary ?? {}
  const providers = global.data?.providers ?? []
  const refreshing = global.isFetching || userRanking.isFetching || modelRanking.isFetching || trend.isFetching

  const refresh = async () => {
    const results = await Promise.all([global.refetch(), userRanking.refetch(), modelRanking.refetch(), trend.refetch()])
    if (results.some((result) => result.isError)) message.error('刷新数据失败')
    else message.success('数据刷新成功')
  }

  const openDetails = () => {
    setDetailsMounted(true)
    setDetailsOpen(true)
  }

  return (
    <div>
      <PageHeader
        title="Token使用统计"
        onBack={() => globalThis.history.back()}
        extra={
          <Space wrap>
            <DatePicker.RangePicker
              aria-label="统计日期范围"
              value={dateRange}
              presets={rangePresets()}
              allowClear={false}
              onChange={(dates) => {
                if (dates?.[0] && dates[1]) setDateRange([dates[0], dates[1]])
              }}
            />
            <Button icon={<UnorderedListOutlined />} onClick={openDetails}>
              显示明细
            </Button>
            <Button type="primary" icon={<ReloadOutlined />} loading={refreshing} onClick={() => void refresh()}>
              刷新
            </Button>
          </Space>
        }
      />

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="总请求次数" value={summary.total_requests || 0} prefix={<ApiOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="总Token消耗" value={summary.total_tokens || 0} suffix="tokens" />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="总成本"
              value={summary.total_cost || 0}
              precision={2}
              prefix="$"
              styles={{ content: { color: 'var(--ant-color-error)' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="活跃用户" value={summary.active_users || 0} prefix={<UserOutlined />} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={12}>
          <Card title="使用趋势" loading={trend.isFetching}>
            <TrendChart points={trend.data ?? []} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="模型使用分布" loading={modelRanking.isFetching}>
            <ModelDistributionChart models={modelRanking.data ?? []} />
          </Card>
        </Col>
      </Row>

      <Card title="供应商成本分析" className={styles.section}>
        <Table<ProviderCost>
          rowKey="provider"
          columns={providerColumns}
          dataSource={providers}
          loading={global.isFetching}
          pagination={false}
          size="small"
        />
      </Card>

      <Row gutter={[16, 16]} className={styles.section}>
        <Col xs={24} lg={12}>
          <Card title="用户排行榜">
            <Table<UserRankingRow>
              rowKey="user_id"
              columns={userColumns}
              dataSource={userRanking.data}
              loading={userRanking.isFetching}
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="模型排行榜">
            <Table<ModelRankingRow>
              rowKey={(row) => `${row.provider}/${row.model}`}
              columns={modelColumns}
              dataSource={modelRanking.data}
              loading={modelRanking.isFetching}
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {detailsMounted && (
        <TokenUsageDetailsModal
          open={detailsOpen}
          initialDateRange={dateRange}
          providerOptions={providers.map((item) => item.provider)}
          onClose={() => setDetailsOpen(false)}
        />
      )}
    </div>
  )
}
